/** Explicit dataset mapping helpers. No header inference or JEV matching. ERP-273. */

export interface DatasetFileHeaders {
  readonly headers: readonly string[];
  readonly sheetCount: number;
}

type DatasetFile = { readonly name: string; arrayBuffer(): Promise<ArrayBuffer> };

const FIELD_LABELS: Readonly<Record<string, string>> = {
  code: 'کد',
  name: 'نام',
  kind: 'نوع طرف‌حساب',
  status: 'وضعیت',
  nationalId: 'شناسه ملی / کد ملی',
  economicCode: 'کد اقتصادی',
  postalCode: 'کد پستی',
  address: 'آدرس',
  mobile: 'موبایل',
  phone: 'تلفن',
  email: 'ایمیل',
  role: 'نقش طرف‌حساب',
  roles: 'نقش‌های طرف‌حساب',
  baseUomCode: 'کد واحد پایه',
};

export function datasetFieldLabel(field: string): string {
  return Object.hasOwn(FIELD_LABELS, field) ? FIELD_LABELS[field]! : field;
}

function validateHeaders(headers: readonly string[]): string[] {
  if (headers.length === 0 || headers.some((header) => header === '')) {
    throw new Error(
      'ردیف اول فایل باید عنوان داشته باشد؛ ستون بدون عنوان را حذف یا نام‌گذاری کنید.',
    );
  }
  const seen = new Set<string>();
  for (const header of headers) {
    if (seen.has(header)) {
      throw new Error(`عنوان ستون «${header}» تکراری است؛ برای هر ستون عنوان یکتا بگذارید.`);
    }
    seen.add(header);
  }
  return [...headers];
}

// Delimiter choice, BOM handling and whitespace follow the staged-row server parser.
function splitCsvLine(line: string, delimiter: ',' | ';'): string[] {
  const cells: string[] = [];
  let current = '';
  let quoted = false;
  for (let index = 0; index < line.length; index += 1) {
    const char = line[index]!;
    if (char === '"') {
      if (quoted && line[index + 1] === '"') {
        current += '"';
        index += 1;
      } else quoted = !quoted;
    } else if (char === delimiter && !quoted) {
      cells.push(current);
      current = '';
    } else current += char;
  }
  if (quoted) {
    throw new Error('CSV با سلول چندخطی یا نقل‌قول ناقص پشتیبانی نمی‌شود؛ از XLSX استفاده کنید.');
  }
  cells.push(current);
  return cells;
}

export function readDatasetCsvHeaders(text: string): string[] {
  const lines = text
    .replace(/^\uFEFF/, '')
    .split(/\r?\n/)
    .filter((line) => line.trim() !== '');
  const first = lines[0] ?? '';
  const comma = splitCsvLine(first, ',');
  const semicolon = splitCsvLine(first, ';');
  const delimiter = semicolon.length > comma.length ? ';' : ',';
  // The backend parses physical lines, not multiline quoted records: fail before upload.
  for (const line of lines.slice(1)) splitCsvLine(line, delimiter);
  return validateHeaders((delimiter === ';' ? semicolon : comma).map((cell) => cell.trim()));
}

function cellText(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  if (typeof value === 'object' && 'text' in value) return String(value.text);
  if (typeof value === 'object' && 'result' in value) return cellText(value.result);
  return '';
}

export async function readDatasetFileHeaders(file: DatasetFile): Promise<DatasetFileHeaders> {
  const bytes = await file.arrayBuffer();
  if (file.name.toLowerCase().endsWith('.xlsx')) {
    const { default: ExcelJS } = await import('exceljs');
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(bytes);
    const sheet = workbook.worksheets[0];
    if (!sheet) throw new Error('فایل Excel هیچ شیتی ندارد.');
    const row = sheet.getRow(1);
    const headers = Array.from({ length: row.cellCount }, (_, index) =>
      cellText(row.getCell(index + 1).value).trim(),
    );
    // Trailing formatted empty cells are harmless. Interior gaps would shift server columns.
    while (headers.length > 0 && headers[headers.length - 1] === '') headers.pop();
    return { headers: validateHeaders(headers), sheetCount: workbook.worksheets.length };
  }
  if (!file.name.toLowerCase().endsWith('.csv')) {
    throw new Error('فقط فایل CSV یا XLSX پذیرفته می‌شود.');
  }
  let text: string;
  try {
    text = new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    throw new Error('فایل CSV باید با کدگذاری UTF-8 ذخیره شود.');
  }
  return { headers: readDatasetCsvHeaders(text), sheetCount: 1 };
}

/** API direction is target field -> source header, never its inverse. */
export function buildDefaultDatasetMapping(
  headers: readonly string[],
  writableFields: readonly string[],
): Record<string, string> {
  const available = new Set(headers);
  return Object.fromEntries(
    writableFields.filter((field) => available.has(field)).map((field) => [field, field]),
  );
}

export function validateDatasetMapping(
  mapping: Readonly<Record<string, unknown>>,
  headers: readonly string[],
  writableFields: readonly string[],
  requiredFields: readonly string[],
): readonly string[] {
  const errors: string[] = [];
  const sources = new Set(headers);
  const writable = new Set(writableFields);
  const used = new Set<string>();
  for (const [field, source] of Object.entries(mapping)) {
    if (!writable.has(field))
      errors.push(`فیلد «${datasetFieldLabel(field)}» برای این ورود مجاز نیست.`);
    if (typeof source !== 'string' || !sources.has(source)) {
      errors.push(`ستون انتخاب‌شده برای «${datasetFieldLabel(field)}» در فایل وجود ندارد.`);
    } else if (used.has(source)) {
      errors.push(`ستون «${source}» به بیش از یک فیلد متصل شده است.`);
    } else used.add(source);
  }
  for (const field of requiredFields) {
    const source = mapping[field];
    if (typeof source !== 'string' || !sources.has(source) || !writable.has(field)) {
      errors.push(`ستون «${datasetFieldLabel(field)}» الزامی است؛ آن را به یک ستون فایل وصل کنید.`);
    }
  }
  return errors;
}
